import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { COUPON_FORM_STEP_ICONS } from "constants/couponFormUi";
import routes from "constants/routes";
import {
  COUPON_FORM_STEPS,
  COUPON_FORM_VIEW_SUBTITLES,
  COUPON_LOCKED_IN_EDIT_FIELDS,
  COUPON_PREVIEW_NOT_SET,
  COUPON_STATUS,
  COUPON_CATEGORY_OPTIONS,
  COUPON_VALIDATION,
  COUPON_USAGE_OPERATIONS,
  COUPON_USAGE_OPERATION_OPTIONS,
  DISCOUNT_TYPE_OPTIONS,
  PLATFORM_ENUM_OPTIONS,
  PLATFORM_OPTIONS,
  DISCOUNT_TYPES,
} from "constants/coupons";
import {
  formatCouponDate,
  formatCouponDateRange,
  getCouponCategoryLabel,
  getCouponStatusLabel,
  resolveCouponStatus,
} from "utils/couponUtils";
import { getLocalIsoDate } from "helpers/couponApi";
import PackageListView from "components/PackageListView/PackageListView";
import PackageSelector from "components/PackageSelector/PackageSelector";
import PlatformListView from "components/PlatformListView/PlatformListView";
import PlatformSelector from "components/PlatformSelector/PlatformSelector";
import {
  getCurrentTotalCouponCount,
  getMaxIncreaseCount,
  getUsageCountChangeMaxError,
  validateCreateCouponForm,
  validateEditCouponForm,
} from "helpers/couponValidation";
import {
  normalizeDecimalInput,
  normalizeIntegerInput,
} from "helpers/numericInput";
import { parsePackageIds, parsePlatformIds } from "helpers/packageIds";
import { useBasicPackagesInfo } from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import CouponFormTopTabs from "./CouponFormTopTabs";
import FormSection from "./FormSection";
import styles from "./CouponForm.module.scss";

const getStepConfig = (stepId) =>
  COUPON_FORM_STEPS.find((step) => step.id === stepId) ?? {};

const STATUS_DOT_CLASS = {
  [COUPON_STATUS.ACTIVE]: styles.statusDotActive,
  [COUPON_STATUS.INACTIVE]: styles.statusDotInactive,
  [COUPON_STATUS.EXPIRED]: styles.statusDotExpired,
  [COUPON_STATUS.SCHEDULED]: styles.statusDotScheduled,
};

const STATUS_BADGE_CLASS = {
  [COUPON_STATUS.ACTIVE]: styles.summaryStatusActive,
  [COUPON_STATUS.INACTIVE]: styles.summaryStatusInactive,
  [COUPON_STATUS.EXPIRED]: styles.summaryStatusExpired,
  [COUPON_STATUS.SCHEDULED]: styles.summaryStatusScheduled,
};

const isFieldLocked = (mode, fieldName) => {
  if (mode === "view") return true;
  if (mode === "edit") return COUPON_LOCKED_IN_EDIT_FIELDS.includes(fieldName);
  return false;
};

const isEmptyPreviewValue = (value) =>
  value === null || value === undefined || value === "";

const getPreviewText = (value) =>
  isEmptyPreviewValue(value) ? COUPON_PREVIEW_NOT_SET : String(value);

const getDiscountDisplay = (values) => {
  if (isEmptyPreviewValue(values.discountValue)) {
    return COUPON_PREVIEW_NOT_SET;
  }

  if (values.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    return `${values.discountValue}% OFF`;
  }

  return `₹${values.discountValue} OFF`;
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDiscountFieldValue = (values) => {
  if (isEmptyPreviewValue(values.discountValue)) {
    return COUPON_PREVIEW_NOT_SET;
  }

  if (values.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    return `${values.discountValue}%`;
  }

  return `₹${values.discountValue}`;
};

const getPreviewDateValue = (dateStr) =>
  isEmptyPreviewValue(dateStr) ? COUPON_PREVIEW_NOT_SET : formatCouponDate(dateStr);

const getPreviewCreatedOnDisplay = (values, isCreate) => {
  if (!isEmptyPreviewValue(values.createdOn)) {
    return formatCouponDate(values.createdOn);
  }

  if (isCreate) {
    return formatCouponDate(new Date());
  }

  return COUPON_PREVIEW_NOT_SET;
};

const hasPreviewCreatedOn = (values, isCreate) =>
  !isEmptyPreviewValue(values.createdOn) || isCreate;

const formatPreviewAmount = (value) =>
  isEmptyPreviewValue(value) ? COUPON_PREVIEW_NOT_SET : `₹${value}`;

const getPreviewSubtitle = (values) => {
  if (values.selectAllPackages) {
    return "On all packages";
  }

  if (parsePackageIds(values.packageIds).length > 0) {
    return "On selected packages";
  }

  return COUPON_PREVIEW_NOT_SET;
};

const FieldLabel = ({ children, required = false, optional = false }) => (
  <label className={styles.fieldLabel}>
    {children}
    {required && <span className={styles.required}>*</span>}
    {optional && <span className={styles.optional}> (Optional)</span>}
  </label>
);

const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className={`${styles.toggle} ${checked ? styles.toggleOn : ""}`}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
  >
    <span className={styles.toggleThumb} />
  </button>
);

const CouponForm = ({
  mode = "create",
  initialValues,
  onSubmit,
  isSubmitting = false,
}) => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const [activeStep, setActiveStep] = useState(1);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [previewPackageSearch, setPreviewPackageSearch] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);

  const toggleSectionCollapse = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const isSectionCollapsed = (sectionKey) => Boolean(collapsedSections[sectionKey]);

  const isView = mode === "view";
  const isCreate = mode === "create";
  const isEdit = mode === "edit";
  const submitLabel = isCreate ? "Save Coupon" : "Update Coupon";

  const isFormSubmittable = useMemo(() => {
    if (isView) {
      return false;
    }

    const validation = isCreate
      ? validateCreateCouponForm(values)
      : validateEditCouponForm(values);

    return validation.isValid;
  }, [values, isCreate, isView]);

  const getStepSubtitle = (stepId) =>
    isView
      ? COUPON_FORM_VIEW_SUBTITLES[stepId] ?? getStepConfig(stepId).subtitle
      : getStepConfig(stepId).subtitle;

  const getDiscountTypeLabel = (discountType) => {
    if (isEmptyPreviewValue(discountType)) {
      return COUPON_PREVIEW_NOT_SET;
    }

    return (
      DISCOUNT_TYPE_OPTIONS.find((opt) => opt.id === discountType)?.label ??
      discountType
    );
  };

  const renderPreviewValueSpan = (value) => {
    const text = getPreviewText(value);

    return (
      <span
        className={`${styles.summaryValue} ${
          text === COUPON_PREVIEW_NOT_SET ? styles.summaryValueNotSet : ""
        }`}
      >
        {text}
      </span>
    );
  };

  const renderPreviewCategoryValue = () => {
    const text = values.category
      ? getCouponCategoryLabel(values.category)
      : COUPON_PREVIEW_NOT_SET;

    return (
      <span
        className={`${styles.summaryValue} ${
          text === COUPON_PREVIEW_NOT_SET ? styles.summaryValueNotSet : ""
        }`}
      >
        {text}
      </span>
    );
  };

  const formatViewValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    return value;
  };

  const renderAmountDecimalHint = () =>
    !isView ? (
      <p className={styles.fieldHint}>
        Not more than {COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES} decimal
        places.
      </p>
    ) : null;

  const renderDiscountValueHints = () => {
    if (isView || isFieldLocked(mode, "discountValue")) return null;

    return (
      <>
        {renderAmountDecimalHint()}
        {values.discountType === DISCOUNT_TYPES.PERCENTAGE && (
          <p className={styles.fieldHint}>
            Must be greater than 0 and up to{" "}
            {COUPON_VALIDATION.MAX_PERCENTAGE_DISCOUNT}.
          </p>
        )}
      </>
    );
  };

  const AMOUNT_INPUT_PATTERN = /^\d*\.?\d{0,2}$/;

  const isPercentageDiscountOverMax = (value, discountType) => {
    if (discountType !== DISCOUNT_TYPES.PERCENTAGE || value === "") {
      return false;
    }

    const num = Number(value);
    return !Number.isNaN(num) && num > COUPON_VALIDATION.MAX_PERCENTAGE_DISCOUNT;
  };

  const handleUsageCountChange = (value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;

    const normalizedValue = normalizeIntegerInput(value);

    if (
      values.usageOperation === COUPON_USAGE_OPERATIONS.DECREASE &&
      normalizedValue !== ""
    ) {
      const num = Number(normalizedValue);
      if (num > getCurrentTotalCouponCount(values)) return;
    }

    updateField("usageCountChange", normalizedValue);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.usageCountChange;

      if (normalizedValue !== "") {
        const num = Number(normalizedValue);
        if (num < 1) {
          next.usageCountChange = getIntegerMinError("usageCountChange");
        } else {
          const maxError = getUsageCountChangeMaxError(values, normalizedValue);
          if (maxError) {
            next.usageCountChange = maxError;
          }
        }
      }

      return next;
    });
  };

  const getIntegerMinError = (field) => {
    if (field === "usageCountChange") {
      return "Count must be greater than 0.";
    }

    return "Coupon count must be greater than 0.";
  };

  const updateIntegerField = (field, value, { min, max, blockBelowMin = false } = {}) => {
    if (value !== "" && !/^\d+$/.test(value)) return;

    const normalizedValue = normalizeIntegerInput(value);

    if (normalizedValue !== "") {
      const num = Number(normalizedValue);

      if (!Number.isNaN(num)) {
        if (max !== undefined && num > max) return;

        if (min !== undefined && num < min && blockBelowMin) return;
      }
    }

    updateField(field, normalizedValue);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];

      if (normalizedValue !== "" && min !== undefined) {
        const num = Number(normalizedValue);
        if (!Number.isNaN(num) && num < min) {
          next[field] = getIntegerMinError(field);
        }
      }

      return next;
    });
  };

  useEffect(() => {
    if (!isEdit) return;

    const count = Number(values.usageCountChange);
    if (
      values.usageCountChange !== "" &&
      !Number.isNaN(count) &&
      count <= 0
    ) {
      setErrors((prev) => ({
        ...prev,
        usageCountChange: getIntegerMinError("usageCountChange"),
      }));
    }
  }, [isEdit, values.usageCountChange]);

  useEffect(() => {
    if (!isEdit) return;

    const changeCount = values.usageCountChange;
    if (changeCount === "" || changeCount === null || changeCount === undefined) {
      return;
    }

    const count = Number(changeCount);
    if (Number.isNaN(count) || count <= 0) return;

    const maxError = getUsageCountChangeMaxError(values, changeCount);
    setErrors((prev) => {
      const next = { ...prev };
      if (maxError) {
        next.usageCountChange = maxError;
      } else {
        delete next.usageCountChange;
      }
      return next;
    });
  }, [isEdit, values.usageOperation, values.totalCouponCount, values.usageCountChange]);

  const updateAmountField = (field, value) => {
    if (value !== "" && !AMOUNT_INPUT_PATTERN.test(value)) return;

    const normalizedValue = normalizeDecimalInput(value);

    if (
      field === "discountValue" &&
      isPercentageDiscountOverMax(normalizedValue, values.discountType)
    ) {
      return;
    }

    updateField(field, normalizedValue);
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handlePlatformsChange = ({ selectAll, platformIds }) => {
    setValues((prev) => ({
      ...prev,
      selectAllPlatforms: selectAll,
      platformIds,
    }));

    setErrors((prev) => {
      if (!prev.selectAllPlatforms && !prev.platformIds) return prev;
      const next = { ...prev };
      delete next.selectAllPlatforms;
      delete next.platformIds;
      return next;
    });
  };

  const handlePackagesChange = ({ selectAll, packageIds }) => {
    setValues((prev) => ({
      ...prev,
      selectAllPackages: selectAll,
      packageIds,
    }));

    setErrors((prev) => {
      if (!prev.selectAllPackages && !prev.packageIds) return prev;
      const next = { ...prev };
      delete next.selectAllPackages;
      delete next.packageIds;
      return next;
    });
  };

  const updateField = (field, value) => {
    setValues((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "isApplicableForSingleUser" && value === false) {
        next.uamId = "";
      }

      if (field === "selectAllPackages" && value === true) {
        next.packageIds = [];
      }

      if (field === "selectAllPlatforms" && value === true) {
        next.platformIds = [];
      }

      if (field === "discountType" && value === DISCOUNT_TYPES.FLAT) {
        next.maxAllowedAmount = "";
      }

      if (
        field === "discountType" &&
        value === DISCOUNT_TYPES.PERCENTAGE &&
        isPercentageDiscountOverMax(next.discountValue, DISCOUNT_TYPES.PERCENTAGE)
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          discountValue:
            "Percentage discount must be greater than 0 and up to 100.",
        }));
      }

      return next;
    });

    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (isView || isSubmitting) return;

    const stepByField = {
      code: 1,
      category: 1,
      remarks: 1,
      effectiveDate: 2,
      expiryDate: 2,
      discountType: 3,
      discountValue: 3,
      maxAllowedAmount: 3,
      totalCouponCount: 4,
      usageOperation: 4,
      usageCountChange: 4,
      isApplicableMultipleTimes: 4,
      isApplicableForSingleUser: 4,
      uamId: 4,
      minimumPackageMrp: 5,
      minimumCartMrp: 5,
      selectAllPackages: 6,
      packageIds: 6,
      selectAllPlatforms: 7,
      platformIds: 7,
    };

    if (isCreate) {
      const validation = validateCreateCouponForm(values);
      setErrors(validation.errors);

      if (!validation.isValid) {
        openSnackbar("Please fix the highlighted validation errors.", "error");
        const firstErrorField = Object.keys(validation.errors)[0];
        scrollToStep(stepByField[firstErrorField] ?? 1);
        return;
      }
    }

    if (isEdit) {
      const validation = validateEditCouponForm(values);
      setErrors(validation.errors);

      if (!validation.isValid) {
        openSnackbar("Please fix the highlighted validation errors.", "error");
        const firstErrorField = Object.keys(validation.errors)[0];
        scrollToStep(stepByField[firstErrorField] ?? 1);
        return;
      }
    }

    if (onSubmit) {
      await onSubmit(values);
      if (isEdit) {
        navigate(routes.coupons.view(values.code), { replace: true });
      }
      return;
    }

    openSnackbar("Coupon updated successfully", "success");
    navigate(routes.coupons.list);
  };

  const scrollToStep = useCallback((stepId) => {
    setActiveStep(stepId);
    document.getElementById(`step-${stepId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const handleCopyCode = async () => {
    if (!values.code) return;
    try {
      await navigator.clipboard.writeText(values.code);
      setCodeCopied(true);
      openSnackbar("Coupon code copied", "success");
      window.setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      openSnackbar("Failed to copy coupon code", "error");
    }
  };

  const renderCopyCodeButton = () => (
    <button
      type="button"
      className={styles.copyCodeBtn}
      onClick={handleCopyCode}
      aria-label={codeCopied ? "Coupon code copied" : "Copy coupon code"}
      title={codeCopied ? "Copied!" : "Copy coupon code"}
    >
      {codeCopied ? (
        <CheckOutlinedIcon fontSize="small" />
      ) : (
        <ContentCopyOutlinedIcon fontSize="small" />
      )}
    </button>
  );

  const renderFieldIcon = (fieldName) => {
    const Icon = isFieldLocked(mode, fieldName)
      ? LockOutlinedIcon
      : EditOutlinedIcon;
    return <Icon className={styles.fieldIconRight} />;
  };

  const computedStatus = resolveCouponStatus(
    values.effectiveDate,
    values.expiryDate
  );
  const statusLabel = getCouponStatusLabel(computedStatus);

  const validityDays = (() => {
    const start = new Date(values.effectiveDate);
    const end = new Date(values.expiryDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  })();

  const { data: basicPackages = [] } = useBasicPackagesInfo(true);

  const mapPackageDisplayName = (pkg) =>
    pkg.displayName || pkg.packageName || `Package ${pkg.packageId}`;

  const getPreviewPackageItems = () => {
    if (values.selectAllPackages) {
      const allItems =
        isCreate && basicPackages.length
          ? basicPackages.map(mapPackageDisplayName)
          : [];

      return {
        isAll: true,
        count: isCreate ? basicPackages.length : values.packagesCount ?? null,
        items: allItems,
      };
    }

    const ids = parsePackageIds(values.packageIds);
    if (ids.length) {
      return {
        isAll: false,
        items: ids.map(
          (id) =>
            basicPackages.find((pkg) => pkg.packageId === id)?.displayName ??
            `Package ${id}`
        ),
      };
    }

    if (values.packagesCount) {
      return {
        isAll: false,
        items: [`${values.packagesCount} packages selected`],
      };
    }

    return { isAll: false, items: [] };
  };

  const getPreviewPlatformItems = () => {
    if (values.selectAllPlatforms) {
      return PLATFORM_ENUM_OPTIONS.map((platform) => platform.label);
    }

    const ids = parsePlatformIds(values.platformIds);
    if (ids.length) {
      return ids.map(
        (id) =>
          PLATFORM_ENUM_OPTIONS.find((platform) => platform.platformId === id)
            ?.label ?? `Platform ${id}`
      );
    }

    return (values.platforms ?? [])
      .map((p) => PLATFORM_OPTIONS.find((o) => o.id === p)?.label ?? p)
      .filter(Boolean);
  };

  const getValidityPreviewItems = () => [
    {
      label: "Effective Date",
      value: getPreviewDateValue(values.effectiveDate),
    },
    {
      label: "Expiry Date",
      value: getPreviewDateValue(values.expiryDate),
    },
  ];

  const getDiscountPreviewItems = () => {
    const items = [
      {
        label: "Type",
        value: getDiscountTypeLabel(values.discountType),
      },
      {
        label: "Value",
        value: formatDiscountFieldValue(values),
      },
    ];

    if (values.discountType === DISCOUNT_TYPES.PERCENTAGE) {
      const maxAmount = values.maxAllowedAmount ?? values.maxDiscount;
      items.push({
        label: "Max allowed discount",
        value: formatPreviewAmount(maxAmount),
      });
    }

    return items;
  };

  const getUsagePreviewItems = () => {
    if (isCreate) {
      const items = [
        {
          label: "Total coupon count",
          value: getPreviewText(values.totalCouponCount),
        },
        {
          label: "Applicable multiple times",
          value: values.isApplicableMultipleTimes ? "Yes" : "No",
        },
        {
          label: "Single user only",
          value: values.isApplicableForSingleUser ? "Yes" : "No",
        },
      ];

      if (values.isApplicableForSingleUser) {
        items.push({
          label: "UAM ID",
          value: getPreviewText(values.uamId),
        });
      }

      return items;
    }

    const total = values.totalCouponCount ?? values.totalUsageLimit;
    const used = values.usedCount ?? 0;

    const items = [
      {
        label: "Times used",
        value: total
          ? `${used} / ${total}`
          : getPreviewText(values.totalCouponCount ?? values.totalUsageLimit),
      },
    ];

    if (values.perUserLimit) {
      items.push({
        label: "Per user limit",
        value: `${values.perUserLimit} time${values.perUserLimit > 1 ? "s" : ""}`,
      });
    }

    return items;
  };

  const renderSummaryBulletList = (
    items,
    { scrollable = false, packageList = false } = {}
  ) => {
    if (!items.length) {
      return (
        <span
          className={`${styles.summaryValue} ${styles.summaryValueNotSet}`}
        >
          {COUPON_PREVIEW_NOT_SET}
        </span>
      );
    }

    const useScroll = scrollable || items.length > 4;
    const scrollClass = packageList
      ? styles.summaryPackageListScroll
      : styles.summaryBulletListScroll;

    return (
      <ul
        className={`${styles.summaryBulletList} ${
          useScroll ? scrollClass : ""
        }`}
      >
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className={styles.summaryBulletItem}>
            {item}
          </li>
        ))}
      </ul>
    );
  };

  const renderPackagesPreview = () => {
    const { isAll, count, items } = getPreviewPackageItems();
    const showPackageSearch = items.length >= 4;
    const searchQuery = previewPackageSearch.trim().toLowerCase();
    const filteredItems = searchQuery
      ? items.filter((name) => name.toLowerCase().includes(searchQuery))
      : items;

    if (!items.length) {
      if (isAll) {
        return (
          <div className={styles.summaryAllBadge}>
            <span className={styles.summaryValue}>All packages</span>
            {count ? (
              <span className={styles.summaryCountPill}>{count} total</span>
            ) : null}
          </div>
        );
      }
      return (
        <span
          className={`${styles.summaryValue} ${styles.summaryValueNotSet}`}
        >
          {COUPON_PREVIEW_NOT_SET}
        </span>
      );
    }

    return (
      <div className={styles.summaryCompactGroup}>
        <div className={styles.summaryAllBadge}>
          <span className={styles.summaryValue}>
            {isAll ? "All packages" : "Selected packages"}
          </span>
          <span className={styles.summaryCountPill}>
            {count ?? items.length} total
          </span>
        </div>
        {showPackageSearch ? (
          <div className={styles.previewSearchWrap}>
            <SearchOutlinedIcon
              className={styles.previewSearchIcon}
              fontSize="small"
            />
            <input
              type="text"
              className={styles.previewSearchInput}
              placeholder="Search packages..."
              value={previewPackageSearch}
              onChange={(event) => setPreviewPackageSearch(event.target.value)}
            />
          </div>
        ) : null}
        {filteredItems.length ? (
          renderSummaryBulletList(filteredItems, {
            scrollable: true,
            packageList: true,
          })
        ) : (
          <p className={styles.previewSearchEmpty}>No packages match search.</p>
        )}
        {searchQuery && filteredItems.length ? (
          <p className={styles.previewSearchHint}>
            Showing {filteredItems.length} of {items.length}
          </p>
        ) : null}
      </div>
    );
  };

  const renderSummaryDetailList = (items) => (
    <div className={styles.summaryDetailList}>
      {items.map((item) => (
        <div key={item.label} className={styles.summaryDetailItem}>
          <span className={styles.summaryDetailKey}>{item.label}</span>
          <span
            className={`${styles.summaryDetailValue} ${
              item.value === COUPON_PREVIEW_NOT_SET ? styles.summaryValueNotSet : ""
            }`}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );

  const previewPackageData = getPreviewPackageItems();
  const previewPlatformItems = getPreviewPlatformItems();

  const renderFieldError = (fieldName) =>
    errors[fieldName] ? (
      <p className={styles.fieldError}>{errors[fieldName]}</p>
    ) : null;

  const getYesNoLabel = (value) => (value ? "Yes" : "No");

  const renderYesNoToggle = (fieldName, { disabled = false } = {}) => {
    const value = values[fieldName];
    const isChecked = value === true;
    const isDisabled = disabled || isView;

    return (
      <div className={styles.toggleRow}>
        <ToggleSwitch
          checked={isChecked}
          onChange={(checked) => updateField(fieldName, checked)}
          disabled={isDisabled}
        />
        <span className={styles.toggleLabel}>{getYesNoLabel(value)}</span>
      </div>
    );
  };

  const renderFieldInput = (
    fieldName,
    children,
    locked = null,
    showIcon = true,
    extraClassName = ""
  ) => {
    const isLocked = locked ?? isFieldLocked(mode, fieldName);
    return (
      <div
        className={`${styles.fieldInput} ${
          isLocked ? styles.fieldInputLocked : ""
        } ${errors[fieldName] ? styles.fieldInputError : ""} ${extraClassName}`}
      >
        {children}
        {showIcon && renderFieldIcon(fieldName)}
      </div>
    );
  };

  const renderReadOnlyField = (fieldName, displayValue) =>
    renderFieldInput(
      fieldName,
      <span className={styles.viewFieldValue}>
        {formatViewValue(displayValue)}
      </span>,
      true,
      true
    );

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        <div className={styles.formColumn}>
          <div className={styles.formStepNavSticky}>
            <CouponFormTopTabs
              steps={COUPON_FORM_STEPS}
              stepIcons={COUPON_FORM_STEP_ICONS}
              activeStep={activeStep}
              onStepClick={scrollToStep}
            />
          </div>
          <div className={styles.formCard}>
          <FormSection
            stepId={1}
            title={getStepConfig(1).title}
            subtitle={getStepSubtitle(1)}
            icon={COUPON_FORM_STEP_ICONS[1]}
            isCollapsed={isSectionCollapsed(1)}
            onToggle={toggleSectionCollapse}
          >
            <div className={styles.fieldGrid}>
              <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                <FieldLabel required>Coupon Code</FieldLabel>
                {isView ? (
                  renderReadOnlyField("code", values.code)
                ) : (
                  renderFieldInput(
                    "code",
                    <input
                      type="text"
                      value={values.code}
                      onChange={(e) =>
                        updateField("code", e.target.value.toUpperCase())
                      }
                      disabled={isFieldLocked(mode, "code")}
                      placeholder="e.g. ALLPACK5008765"
                      maxLength={COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH}
                    />
                  )
                )}
                {renderFieldError("code")}
                {!isCreate && !isView && (
                  <p className={styles.fieldHint}>
                    Coupon code cannot be changed.
                  </p>
                )}
                {isCreate && (
                  <p className={styles.fieldHint}>
                    Letters, numbers, hyphen, and underscore only. Max{" "}
                    {COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH} characters.
                  </p>
                )}
              </div>
              <div className={styles.fieldGroup}>
                <FieldLabel required>Coupon Category</FieldLabel>
                {renderFieldInput(
                  "category",
                  isView || isFieldLocked(mode, "category") ? (
                    <span>{getCouponCategoryLabel(values.category)}</span>
                  ) : (
                    <select
                      value={values.category ?? ""}
                      onChange={(e) => updateField("category", e.target.value)}
                    >
                      <option value="">Select category</option>
                      {COUPON_CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ),
                  isView || isFieldLocked(mode, "category"),
                  isView || isFieldLocked(mode, "category")
                )}
                {renderFieldError("category")}
                {isEdit && !isView && (
                  <p className={styles.fieldHint}>
                    Coupon category cannot be changed.
                  </p>
                )}
              </div>
              <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                <FieldLabel required>Remarks</FieldLabel>
                {isView ? (
                  renderReadOnlyField("remarks", values.remarks)
                ) : (
                  renderFieldInput(
                    "remarks",
                    <textarea
                      className={styles.remarksTextarea}
                      value={values.remarks ?? ""}
                      onChange={(e) => updateField("remarks", e.target.value)}
                      maxLength={COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH}
                      placeholder="Internal remarks for coupon context"
                      rows={3}
                    />,
                    null,
                    true,
                    styles.fieldInputMultiline
                  )
                )}
                {renderFieldError("remarks")}
                {!isView && (
                  <p className={styles.fieldHint}>
                    {(values.remarks ?? "").length}/
                    {COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH} characters
                  </p>
                )}
              </div>
            </div>
          </FormSection>

          <FormSection
            stepId={2}
            title={getStepConfig(2).title}
            subtitle={getStepSubtitle(2)}
            icon={COUPON_FORM_STEP_ICONS[2]}
            isCollapsed={isSectionCollapsed(2)}
            onToggle={toggleSectionCollapse}
          >
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <FieldLabel required>Effective Date</FieldLabel>
                {renderFieldInput(
                  "effectiveDate",
                  isFieldLocked(mode, "effectiveDate") ? (
                    <span>{formatDisplayDate(values.effectiveDate)}</span>
                  ) : (
                    <input
                      type="date"
                      value={values.effectiveDate || ""}
                      min={isCreate ? getLocalIsoDate() : undefined}
                      onChange={(e) =>
                        updateField("effectiveDate", e.target.value)
                      }
                    />
                  )
                )}
                {renderFieldError("effectiveDate")}
                {!isCreate && !isView && (
                  <p className={styles.fieldHint}>
                    Effective date cannot be changed.
                  </p>
                )}
                {isCreate && (
                  <p className={styles.fieldHint}>
                    Must be today or within{" "}
                    {COUPON_VALIDATION.MAX_ALLOWED_EFFECTIVE_DATE_OFFSET_DAYS}{" "}
                    days.
                  </p>
                )}
              </div>
              <div className={styles.fieldGroup}>
                <FieldLabel required>Expiry Date</FieldLabel>
                {isView ? (
                  renderReadOnlyField(
                    "expiryDate",
                    formatDisplayDate(values.expiryDate)
                  )
                ) : (
                  renderFieldInput(
                    "expiryDate",
                    <input
                      type="date"
                      value={values.expiryDate}
                      onChange={(e) => updateField("expiryDate", e.target.value)}
                    />
                  )
                )}
                {renderFieldError("expiryDate")}
                {!isCreate && !isView && (
                  <p className={styles.fieldHint}>
                    You can update the expiry date.
                  </p>
                )}
                {isCreate && (
                  <p className={styles.fieldHint}>
                    Must be 1–
                    {COUPON_VALIDATION.MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS} days
                    after effective date.
                  </p>
                )}
              </div>
              {!isCreate && (
                <div className={styles.fieldGroup}>
                  <FieldLabel>Status</FieldLabel>
                  <div className={styles.statusSelectWrap}>
                    <span
                      className={`${styles.statusDot} ${
                        STATUS_DOT_CLASS[computedStatus] ?? styles.statusDotActive
                      }`}
                    />
                    <span>{statusLabel}</span>
                  </div>
                  {!isView && (
                    <p className={styles.fieldHint}>
                      Status is determined from the effective and expiry dates.
                    </p>
                  )}
                </div>
              )}
              {validityDays !== null && (
                <div className={`${styles.fieldGroup} ${styles.validitySummary}`}>
                  <FieldLabel>Validity Summary</FieldLabel>
                  <div className={styles.validitySummaryBox}>
                    <span className={styles.validitySummaryValue}>
                      {validityDays} day{validityDays === 1 ? "" : "s"}
                    </span>
                    <span className={styles.validitySummaryHint}>
                      Between effective and expiry date
                    </span>
                  </div>
                </div>
              )}
            </div>
          </FormSection>

          <FormSection
            stepId={3}
            title={getStepConfig(3).title}
            subtitle={getStepSubtitle(3)}
            icon={COUPON_FORM_STEP_ICONS[3]}
            isCollapsed={isSectionCollapsed(3)}
            onToggle={toggleSectionCollapse}
          >
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <FieldLabel required>Discount Type</FieldLabel>
                {renderFieldInput(
                  "discountType",
                  isFieldLocked(mode, "discountType") ? (
                    <span>{getDiscountTypeLabel(values.discountType)}</span>
                  ) : (
                    <select
                      value={values.discountType ?? ""}
                      onChange={(e) =>
                        updateField("discountType", e.target.value)
                      }
                    >
                      {isCreate && (
                        <option value="">Select discount type</option>
                      )}
                      {DISCOUNT_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ),
                  isFieldLocked(mode, "discountType"),
                  isFieldLocked(mode, "discountType")
                )}
                {renderFieldError("discountType")}
              </div>
              <div className={styles.fieldGroup}>
                <FieldLabel required>Discount Value</FieldLabel>
                {renderFieldInput(
                  "discountValue",
                  isFieldLocked(mode, "discountValue") ? (
                    <span>{formatDiscountFieldValue(values)}</span>
                  ) : (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={values.discountValue ?? ""}
                      placeholder="Enter discount value"
                      onChange={(e) =>
                        updateAmountField("discountValue", e.target.value)
                      }
                    />
                  )
                )}
                {renderFieldError("discountValue")}
                {renderDiscountValueHints()}
              </div>
              {values.discountType === DISCOUNT_TYPES.PERCENTAGE && (
                <div className={styles.fieldGroup}>
                  <FieldLabel required>Max Allowed Amount</FieldLabel>
                  {renderFieldInput(
                    "maxAllowedAmount",
                    isView ? (
                      <span>₹ {values.maxAllowedAmount}</span>
                    ) : (
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Enter amount"
                        value={values.maxAllowedAmount ?? ""}
                        onChange={(e) =>
                          updateAmountField("maxAllowedAmount", e.target.value)
                        }
                      />
                    ),
                    isView
                  )}
                  {renderFieldError("maxAllowedAmount")}
                  {!isView && renderAmountDecimalHint()}
                </div>
              )}
            </div>
          </FormSection>

          <FormSection
            stepId={4}
            title={getStepConfig(4).title}
            subtitle={getStepSubtitle(4)}
            icon={COUPON_FORM_STEP_ICONS[4]}
            isCollapsed={isSectionCollapsed(4)}
            onToggle={toggleSectionCollapse}
          >
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <FieldLabel required>
                  {isEdit ? "Current Total Coupon Count" : "Total Coupon Count"}
                </FieldLabel>
                {isView || isEdit ? (
                  renderReadOnlyField("totalCouponCount", values.totalCouponCount)
                ) : (
                  renderFieldInput(
                    "totalCouponCount",
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Enter count"
                      value={values.totalCouponCount ?? ""}
                      onChange={(e) =>
                        updateIntegerField("totalCouponCount", e.target.value, {
                          min: COUPON_VALIDATION.MIN_TOTAL_COUPON_COUNT,
                          max: COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT,
                          blockBelowMin: true,
                        })
                      }
                    />
                  )
                )}
                {renderFieldError("totalCouponCount")}
                {isCreate && (
                  <p className={styles.fieldHint}>
                    Must be greater than 0. Maximum{" "}
                    {COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT.toLocaleString()}.
                  </p>
                )}
              </div>
              {isEdit && (
                <div
                  className={`${styles.fieldGroup} ${styles.fieldGroupFull} ${styles.relatedFieldRow}`}
                >
                  <div className={styles.fieldGroup}>
                    <FieldLabel required>Operation</FieldLabel>
                    {renderFieldInput(
                      "usageOperation",
                      <select
                        value={values.usageOperation ?? ""}
                        onChange={(e) =>
                          updateField("usageOperation", e.target.value)
                        }
                      >
                        {COUPON_USAGE_OPERATION_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                      </select>,
                      false,
                      false
                    )}
                    {renderFieldError("usageOperation")}
                  </div>
                  <div className={styles.fieldGroup}>
                    <FieldLabel required>Count</FieldLabel>
                    {renderFieldInput(
                      "usageCountChange",
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="0"
                        value={values.usageCountChange ?? ""}
                        onChange={(e) =>
                          handleUsageCountChange(e.target.value)
                        }
                      />
                    )}
                    {renderFieldError("usageCountChange")}
                  </div>
                  <p className={`${styles.fieldHint} ${styles.relatedFieldHint}`}>
                    Must be greater than 0.{" "}
                    {values.usageOperation === COUPON_USAGE_OPERATIONS.INCREASE
                      ? `You can increase by up to ${getMaxIncreaseCount(values).toLocaleString()} (current total ${getCurrentTotalCouponCount(values).toLocaleString()}, maximum ${COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT.toLocaleString()}).`
                      : `You can decrease by up to ${getCurrentTotalCouponCount(values).toLocaleString()}.`}
                  </p>
                </div>
              )}
              <div className={styles.fieldGroup}>
                <FieldLabel required>Applicable multiple times</FieldLabel>
                {renderYesNoToggle("isApplicableMultipleTimes")}
                {renderFieldError("isApplicableMultipleTimes")}
              </div>
              <div className={styles.fieldGroup}>
                <FieldLabel required>Is Applicable For Single User</FieldLabel>
                {renderYesNoToggle("isApplicableForSingleUser", {
                  disabled: isEdit,
                })}
                {renderFieldError("isApplicableForSingleUser")}
                {isEdit && !isView && (
                  <p className={styles.fieldHint}>
                    Single-user applicability cannot be changed.
                  </p>
                )}
              </div>
              {values.isApplicableForSingleUser && (
                <div className={styles.fieldGroup}>
                  <FieldLabel required>UAM ID</FieldLabel>
                  {isView ? (
                    renderReadOnlyField("uamId", values.uamId)
                  ) : (
                    renderFieldInput(
                      "uamId",
                      <input
                        type="number"
                        value={values.uamId ?? ""}
                        onChange={(e) =>
                          updateIntegerField("uamId", e.target.value)
                        }
                      />
                    )
                  )}
                  {renderFieldError("uamId")}
                </div>
              )}
            </div>
          </FormSection>

          <FormSection
            stepId={5}
            title={getStepConfig(5).title}
            subtitle={getStepSubtitle(5)}
            icon={COUPON_FORM_STEP_ICONS[5]}
            isCollapsed={isSectionCollapsed(5)}
            onToggle={toggleSectionCollapse}
          >
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <FieldLabel required>Minimum Package MRP</FieldLabel>
                {renderFieldInput(
                  "minimumPackageMrp",
                  isView ? (
                    <span>₹ {values.minimumPackageMrp}</span>
                  ) : (
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Enter amount"
                      value={values.minimumPackageMrp ?? ""}
                      onChange={(e) =>
                        updateAmountField("minimumPackageMrp", e.target.value)
                      }
                    />
                  ),
                  isView
                )}
                {renderFieldError("minimumPackageMrp")}
                {!isView && renderAmountDecimalHint()}
              </div>
              <div className={styles.fieldGroup}>
                <FieldLabel optional>Minimum Cart MRP</FieldLabel>
                {renderFieldInput(
                  "minimumCartMrp",
                  isView ? (
                    <span>
                      {values.minimumCartMrp !== "" &&
                      values.minimumCartMrp !== null &&
                      values.minimumCartMrp !== undefined
                        ? `₹ ${values.minimumCartMrp}`
                        : "—"}
                    </span>
                  ) : (
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Enter amount"
                      value={values.minimumCartMrp ?? ""}
                      onChange={(e) =>
                        updateAmountField("minimumCartMrp", e.target.value)
                      }
                    />
                  ),
                  isView
                )}
                {renderFieldError("minimumCartMrp")}
                {!isView && renderAmountDecimalHint()}
              </div>
            </div>
          </FormSection>

          <FormSection
            stepId={6}
            title={getStepConfig(6).title}
            subtitle={getStepSubtitle(6)}
            icon={COUPON_FORM_STEP_ICONS[6]}
            isCollapsed={isSectionCollapsed(6)}
            onToggle={toggleSectionCollapse}
          >
            {isCreate || isEdit ? (
              <div className={styles.fieldGrid}>
                <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                  <FieldLabel required>Applicable Packages</FieldLabel>
                  <PackageSelector
                    selectAll={Boolean(values.selectAllPackages)}
                    selectedIds={parsePackageIds(values.packageIds)}
                    onChange={handlePackagesChange}
                    error={errors.packageIds || errors.selectAllPackages}
                  />
                </div>
              </div>
            ) : (
              <PackageListView
                selectAll={Boolean(values.selectAllPackages)}
                selectedIds={parsePackageIds(values.packageIds)}
                viewMode
              />
            )}
          </FormSection>

          <FormSection
            stepId={7}
            title={getStepConfig(7).title}
            subtitle={getStepSubtitle(7)}
            icon={COUPON_FORM_STEP_ICONS[7]}
            isCollapsed={isSectionCollapsed(7)}
            onToggle={toggleSectionCollapse}
          >
            {isCreate || isEdit ? (
              <div className={styles.fieldGrid}>
                <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                  <FieldLabel required>Applicable Platforms</FieldLabel>
                  <PlatformSelector
                    selectAll={Boolean(values.selectAllPlatforms)}
                    selectedIds={parsePlatformIds(values.platformIds)}
                    onChange={handlePlatformsChange}
                    error={errors.platformIds || errors.selectAllPlatforms}
                  />
                </div>
              </div>
            ) : (
              <PlatformListView
                selectAll={Boolean(values.selectAllPlatforms)}
                selectedIds={parsePlatformIds(values.platformIds)}
                viewMode
              />
            )}
          </FormSection>

        </div>
        </div>

        <aside className={styles.sidebar}>
          <div id="coupon-preview-panel" className={styles.previewPanel}>
            <FormSection
              sectionKey="previewTicket"
              title="Coupon Preview"
              variant="sidebar"
              isCollapsed={isSectionCollapsed("previewTicket")}
              onToggle={toggleSectionCollapse}
              className={`${styles.sidebarSectionFlush} ${styles.sidebarSectionCard}`}
            >
              <div className={styles.couponPreview}>
                <div className={styles.previewTop}>
                  <div className={styles.previewCodeRow}>
                    <span
                      className={
                        isEmptyPreviewValue(values.code)
                          ? `${styles.previewCode} ${styles.previewCodeNotSet}`
                          : styles.previewCode
                      }
                    >
                      {getPreviewText(values.code)}
                    </span>
                    {values.code ? renderCopyCodeButton() : null}
                  </div>
                  {values.code && codeCopied && (
                    <span className={styles.previewCopiedHint}>Copied!</span>
                  )}
                </div>
                <div className={styles.previewBottom}>
                  <p
                    className={`${styles.previewDiscount} ${
                      getDiscountDisplay(values) === COUPON_PREVIEW_NOT_SET
                        ? styles.summaryValueNotSet
                        : ""
                    }`}
                  >
                    {getDiscountDisplay(values)}
                  </p>
                  <p
                    className={`${styles.previewDesc} ${
                      getPreviewSubtitle(values) === COUPON_PREVIEW_NOT_SET
                        ? styles.summaryValueNotSet
                        : ""
                    }`}
                  >
                    {getPreviewSubtitle(values)}
                  </p>
                </div>
              </div>
            </FormSection>

            <div className={styles.previewScrollArea}>
            <FormSection
              sectionKey="previewSummary"
              title="Coupon Summary"
              variant="sidebar"
              isCollapsed={isSectionCollapsed("previewSummary")}
              onToggle={toggleSectionCollapse}
              className={styles.sidebarSectionPreview}
            >
            <div className={styles.summaryList}>
              <div className={styles.summaryRow}>
                <ConfirmationNumberOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Coupon Code</span>
                  {renderPreviewValueSpan(values.code)}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <LocalOfferOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Category</span>
                  {renderPreviewCategoryValue()}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <InfoOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Remarks</span>
                  {renderPreviewValueSpan(values.remarks)}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <FiberManualRecordIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Status</span>
                  <span
                    className={`${styles.summaryStatusActive} ${
                      STATUS_BADGE_CLASS[computedStatus] ?? ""
                    }`}
                  >
                    {statusLabel}
                  </span>
                </div>
              </div>
              <div className={styles.summaryRow}>
                <DateRangeOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Validity</span>
                  {renderSummaryDetailList(getValidityPreviewItems())}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <LocalOfferOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Discount</span>
                  {renderSummaryDetailList(getDiscountPreviewItems())}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <BarChartOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Usage</span>
                  {renderSummaryDetailList(getUsagePreviewItems())}
                </div>
              </div>
              <div className={styles.summaryRow}>
                  <BarChartOutlinedIcon className={styles.summaryIcon} />
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Eligibility</span>
                    {renderSummaryDetailList([
                      {
                        label: "Min package MRP",
                        value: formatPreviewAmount(values.minimumPackageMrp),
                      },
                      {
                        label: "Min cart MRP",
                        value: formatPreviewAmount(values.minimumCartMrp),
                      },
                    ])}
                  </div>
                </div>
              <div
                className={`${styles.summaryRow} ${styles.summaryRowPackages}`}
              >
                <Inventory2OutlinedIcon className={styles.summaryIcon} />
                <div
                  className={`${styles.summaryContent} ${styles.summaryContentPackages}`}
                >
                  <span className={styles.summaryLabel}>
                    Applicable Packages
                    {!previewPackageData.isAll &&
                    previewPackageData.items.length > 0
                      ? ` (${previewPackageData.items.length})`
                      : ""}
                  </span>
                  {renderPackagesPreview()}
                </div>
              </div>
              <div
                className={`${styles.summaryRow} ${styles.summaryRowPlatforms}`}
              >
                <DevicesOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>
                    Applicable Platforms
                    {previewPlatformItems.length > 0
                      ? ` (${previewPlatformItems.length})`
                      : ""}
                  </span>
                  {renderSummaryBulletList(previewPlatformItems)}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <CalendarTodayOutlinedIcon className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Created On</span>
                  <span
                    className={`${styles.summaryValue} ${
                      hasPreviewCreatedOn(values, isCreate)
                        ? ""
                        : styles.summaryValueNotSet
                    }`}
                  >
                    {getPreviewCreatedOnDisplay(values, isCreate)}
                  </span>
                </div>
              </div>
            </div>
            </FormSection>

            {!isCreate && !isView && (
              <div className={styles.changesNote}>
                <InfoOutlinedIcon className={styles.infoBoxIcon} fontSize="small" />
                <span>Changes will reflect for new orders only.</span>
              </div>
            )}
            </div>

            <div className={styles.sidebarActions}>
              {isView ? (
                <button
                  type="button"
                  className={`${styles.updateCouponBtn} ${styles.updateCouponBtnFull}`}
                  onClick={() => navigate(routes.coupons.edit(values.code))}
                >
                  Edit Coupon
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.updateCouponBtn} ${styles.updateCouponBtnFull}`}
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isFormSubmittable}
                >
                  {isSubmitting ? "Saving..." : submitLabel}
                </button>
              )}
            </div>
          </div>

        </aside>
      </div>
    </div>
  );
};

export default CouponForm;
